import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircle from '@mui/icons-material/AccountCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import SuperAdminBadge from './SuperAdminBadge';
import { RootState } from '../../store';
import { notificationsService, Notification } from '../../services/notificationsService';
import { formatDate } from '../../utils/formatters';

interface AppHeaderProps {
  open: boolean;
  toggleDrawer: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({ open, toggleDrawer }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const theme = useTheme();

  useEffect(() => {
    const handleUnreadCount = (count: number) => {
      setUnreadCount(count);
    };

    notificationsService.startPolling(handleUnreadCount, 30000);

    return () => {
      notificationsService.stopPolling(handleUnreadCount);
    };
  }, []);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    dispatch(logout());
    navigate('/login');
  };

  const handleChangePassword = () => {
    handleClose();
    navigate('/change-password');
  };

  const handleNotificationsClick = async (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget);
    setLoadingNotifications(true);
    try {
      const response = await notificationsService.getNotifications({ page: 1, limit: 10 });
      setNotifications(response.data);
    } catch (error) {
      console.error('Error al cargar notificaciones:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      try {
        await notificationsService.markAsRead(notification.id);
        setUnreadCount((prev) => Math.max(0, prev - 1));
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n)),
        );
      } catch (error) {
        console.error('Error al marcar notificación como leída:', error);
      }
    }

    if (notification.relatedEntityType === 'request' && notification.relatedEntityId) {
      navigate(`/production/requests/${notification.relatedEntityId}`);
      handleNotificationsClose();
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsService.markAllAsRead();
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error al marcar todas como leídas:', error);
    }
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        background: '#212121', // Negro similar al panel superior del equipo
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        borderBottom: '1px solid #333'
      }}
    >
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          aria-label={open ? 'colapsar menú lateral' : 'expandir menú lateral'}
          onClick={toggleDrawer}
          sx={{ mr: 2 }}
        >
          {open ? <MenuOpenIcon /> : <MenuIcon />}
        </IconButton>
        <Typography 
          variant="h6" 
          noWrap 
          component="div" 
          sx={{ 
            flexGrow: 1, 
            fontWeight: 600,
            letterSpacing: '0.5px',
            fontFamily: 'Inter, sans-serif'
          }}
        >
          STAM Platform
        </Typography>
        <Box display="flex" alignItems="center" mr={2}>
          {user && (
            <Typography 
              variant="body2" 
              sx={{ 
                mr: 1,
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.875rem',
                color: '#f0f0f0'
              }}
            >
              {user.firstName} {user.lastName}
            </Typography>
          )}
          <SuperAdminBadge />
        </Box>
        <Box>
          <IconButton
            color="inherit"
            sx={{ color: '#f0f0f0' }}
            onClick={handleNotificationsClick}
            aria-label="notificaciones"
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Menu
            id="notifications-menu"
            anchorEl={notificationsAnchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(notificationsAnchorEl)}
            onClose={handleNotificationsClose}
            PaperProps={{
              sx: {
                backgroundColor: '#212121',
                color: '#f0f0f0',
                border: '1px solid #333',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                maxWidth: 400,
                maxHeight: 500,
                mt: 1,
              },
            }}
          >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Notificaciones
              </Typography>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  startIcon={<DoneAllIcon />}
                  onClick={handleMarkAllAsRead}
                  sx={{ color: '#90caf9' }}
                >
                  Marcar todas
                </Button>
              )}
            </Box>
            <Divider sx={{ borderColor: '#333' }} />
            {loadingNotifications ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No hay notificaciones
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {notifications.map((notification) => (
                  <ListItem
                    key={notification.id}
                    button
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      backgroundColor: notification.read ? 'transparent' : 'rgba(144, 202, 249, 0.08)',
                      '&:hover': {
                        backgroundColor: notification.read
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(144, 202, 249, 0.12)',
                      },
                      borderBottom: '1px solid #333',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: notification.read ? 400 : 600 }}>
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              color: '#b0b0b0',
                              display: 'block',
                              mt: 0.5,
                              mb: 0.5,
                            }}
                          >
                            {notification.message}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: '#808080' }}
                          >
                            {formatDate(notification.createdAt)}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Menu>
          <IconButton
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
            sx={{ color: '#f0f0f0' }}
          >
            <AccountCircle />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                backgroundColor: '#212121',
                color: '#f0f0f0',
                border: '1px solid #333',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              },
            }}
          >
            <MenuItem onClick={handleChangePassword}>Cambiar contraseña</MenuItem>
            <MenuItem onClick={handleLogout}>Cerrar Sesión</MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AppHeader; 